import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Droplets, Flame, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  type Alert,
  type Service,
  type User,
  getMe,
  getAlerts,
  updateNotifications,
} from '../api/client'
import { extractErrorMessage } from '../api/errors'
import { Toggle } from '../components/Toggle'

const SERVICE_ICON: Record<Service, typeof Droplets> = {
  water: Droplets,
  gas: Flame,
  electricity: Zap,
}

export function Home() {
  const { t, i18n } = useTranslation()
  const [user, setUser] = useState<User | null>(null)
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([getMe(), getAlerts(10, 0)])
      .then(([u, list]) => {
        setUser(u)
        setAlerts(list.items)
      })
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [])

  const handleToggle = async (service: Service, next: boolean) => {
    if (!user) return
    const previous = user.notifications
    const optimistic = { ...previous, [service]: next }
    setUser({ ...user, notifications: optimistic })
    try {
      const updated = await updateNotifications(optimistic)
      setUser(updated)
    } catch (err) {
      setUser({ ...user, notifications: previous })
      setError(extractErrorMessage(err))
    }
  }

  if (loading) return <p className="screen__hint">{t('home.loading')}</p>
  if (error && !user) return <p className="screen__error">{error}</p>
  if (!user) return null

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  return (
    <div className="screen">
      <h1 className="screen__title">bayt</h1>

      {error && <p className="screen__error">{error}</p>}

      <Link to="/address" className="card card--link">
        <div className="card__label">{t('home.your_address')}</div>
        {user.address.street ? (
          <div className="card__value">
            {user.address.city ? `${user.address.city}, ` : ''}
            {user.address.street}
          </div>
        ) : (
          <div className="card__cta">{t('home.set_address')}</div>
        )}
      </Link>

      <div className="card">
        <div className="card__label">{t('home.notifications')}</div>
        {(['water', 'gas', 'electricity'] as Service[]).map((s) => (
          <Toggle
            key={s}
            label={t(`services.${s}`)}
            checked={user.notifications[s]}
            onChange={(v) => handleToggle(s, v)}
          />
        ))}
      </div>

      <div className="card">
        <div className="card__label">{t('home.recent_alerts')}</div>
        {alerts.length === 0 ? (
          <div className="card__hint">{t('home.no_alerts')}</div>
        ) : (
          <ul className="alert-list">
            {alerts.map((a) => {
              const Icon = SERVICE_ICON[a.service]
              return (
                <li key={a.id} className="alert">
                  <Icon size={20} className="alert__icon" />
                  <div className="alert__body">
                    <div className="alert__title">{a.title}</div>
                    <div className="alert__time">{formatTime(a.sent_at)}</div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
